import * as d3 from 'd3';
import { dN_Rect, dN_Rect_Erase } from './d_rect';
import {parseShapeOrientation} from './d_rect';
import { shapeTriangle } from './shapes/shapeTriangle';
import {shapeTrapezoid}  from './shapes/shapeTrapezoid';
import {shapeAngledTopTrapezoid}  from './shapes/shapeAngledTopTrapezoid';
import { shapeOctagon } from './shapes/shapeOctagon';
import { shapePentagon } from './shapes/shapePentagon';
import { shapeEyebrow } from './shapes/shapeEyebrow';
import { shapeHalfEyebrow } from './shapes/shapeHalfEyebrow';

import { drawHinge } from './drawHinge';
import { drawGrills } from './drawGrills';
import { getGlassDesign } from './glassPatternsDefs';


export function drawPicture(nSections = 5, indexWindow = 1,
    svg, d = 2, initX = 20, initY = 20, frameWidth = 10, doorData = null, top = false, OneSection = false)
{
    const frameData = doorData?.frameData;
    const finish = doorData?.finish;
    const finishColor = doorData.flipped ? doorData?.finish?.exteriorColor?.value || 'white' :
                                           doorData?.finish?.interiorColor?.value || 'white' ;


    // Access type dynamically
    let innerPoints; 
    let type = 'type';
    let shapePattern = 'shape';
    let windowHeight = parseFloat(frameData.windowHeight_d); 
    if (top){
        type = 'typeTop';
        shapePattern = 'shapeTop';
        windowHeight = parseFloat(frameData?.uHeight_d || 0); 
    }
    const typeKey = `${type}${indexWindow}`;
    const widthKey = `windowWidth${indexWindow}_d`;

    const shapeKey = `${shapePattern}${indexWindow}`;
    const shapeString = frameData?.[shapeKey] || 'Rectangle';
    const  {shape, orientation} = parseShapeOrientation(shapeString); 


    if (frameData?.[typeKey] === 'Picture') {
        let windowWidth = parseFloat(frameData[widthKey]);
        //const windowHeight = parseFloat(frameData.windowHeight_d); // - 2*d;

        let adjustedWidth = (windowWidth - frameWidth * 2) / 2;

        let xOffset = initX ;
        for (let i = 1; i < indexWindow; i++) {
            const key = `windowWidth${i}_d`;
            xOffset += parseFloat(frameData[key] || 0) + 2 * frameWidth + 2*d ;
        }
        
      
        const glassType = `glassType${indexWindow}`;
        const glassColor = doorData?.glass?.[glassType].value || 'white';
        if (OneSection){
            xOffset = initX;
            windowWidth = parseFloat(frameData?.uWidth1_d)  - 
            2 * frameWidth - 2*d + parseInt(nSections);
            adjustedWidth = (windowWidth - 2 * frameWidth)*0.5;
        }
        const glassDesign = getGlassDesign(doorData);

        if (shape !== 'Rectangle') {
            let delta = 0;
            if (top) {delta = 1;}
            dN_Rect_Erase(svg, d, xOffset - 1, initY - delta, [
                { w: windowWidth, h: windowHeight - 0, frameWidth: frameWidth+1 }
            ], 'white');
       }
        if (shape === 'Triangle') {
            innerPoints = shapeTriangle(svg, d, xOffset, initY, [
                {
                    w: windowWidth,
                    h: windowHeight - 1,
                    color: glassColor,
                    glassType: doorData?.glass?.glassType1.label,
                    glassDesign: glassDesign,
                    frameWidth,
                    frameColor: finishColor,
                    lineFrameColor: 'black',
                    lineFrameThickness: 1,
                    outerStrokeColor: 'black',
                    outerStrokeWidth: 1,
                    middleFrame: true,
                    orientation: orientation
                }
            ], {
                labelText: `\u00A0${indexWindow} ✔`,
                subFrame: true,
                labelOptions: {
                    fontSize: 24,
                    fontColor: 'green',
                    fontOpacity: 0.2
                },
                drawFrame: true
            });
        }
        if (shape === 'Trapezoid') {
            // Get the trapezoid size based on orientation and window index
            // Note: The user inputs the TOP WIDTH, but shapeTrapezoid expects X = (bottomWidth - topWidth)
            const trapezoidKey = top ? `trapezoidTop${indexWindow}` : `trapezoid${indexWindow}`;
            let userTopWidth;
            if (orientation === 'left') {
                userTopWidth = parseFloat(doorData?.frameData?.[trapezoidKey]?.leftSize_d);
            } else if (orientation === 'right') {
                userTopWidth = parseFloat(doorData?.frameData?.[trapezoidKey]?.rightSize_d);
            }

            // Calculate X parameter: X = bottomWidth - topWidth
            // If no user input (undefined/NaN), default to windowWidth / 2 (which makes top width = windowWidth / 2)
            // Note: Must check for NaN explicitly because 0 is a valid value
            let trapezoidX = (!isNaN(userTopWidth) && userTopWidth !== undefined) ? (windowWidth - userTopWidth) : windowWidth / 2;

            // Validate trapezoidX to ensure valid trapezoid: must be > 0 and < windowWidth
            // If invalid, clamp to safe range
            if (trapezoidX <= 0) {
                trapezoidX = 1; // Minimum valid value
            } else if (trapezoidX >= windowWidth) {
                trapezoidX = windowWidth - 1; // Maximum valid value
            }

            innerPoints = shapeTrapezoid(svg, d, xOffset, initY, [
                {
                    w: windowWidth,
                    h: windowHeight - 1,
                    X: trapezoidX,  // X = (bottom width - top width)
                    Y: windowHeight - 1, // right side height (same as left for symmetric trapezoid)
                    color: glassColor,
                    glassType: doorData?.glass?.glassType1.label,
                    glassDesign: glassDesign,
                    frameWidth,
                    frameColor: finishColor,
                    lineFrameColor: 'black',
                    lineFrameThickness: 1,
                    outerStrokeColor: 'black',
                    outerStrokeWidth: 1,
                    middleFrame: true,
                    orientation: orientation
                }
            ], {
                labelText: `\u00A0${indexWindow} ✔`,
                subFrame: true,
                labelOptions: {
                    fontSize: 24,
                    fontColor: 'green',
                    fontOpacity: 0.2
                },
                drawFrame: true
            });
        }
        if (shape === 'AngledTopTrapezoid') {
            // Get the angled trapezoid size based on orientation and window index
            const angledTrapezoidKey = top ? `angledTopTrapezoidTop${indexWindow}` : `angledTopTrapezoid${indexWindow}`;
            let y1, y2;
            let angledTopSize;

            if (orientation === 'left'){
                angledTopSize = parseFloat(doorData?.frameData?.[angledTrapezoidKey]?.leftSize_d);
                // Check for NaN explicitly because 0 is a valid value
                y1 = (!isNaN(angledTopSize) && angledTopSize !== undefined) ? angledTopSize : (windowHeight - 1) / 2;
                y2 = 0;

                // Validate y1 to ensure valid trapezoid: must be >= 0 and < windowHeight
                if (y1 < 0) {
                    y1 = 0;
                } else if (y1 >= windowHeight - 1) {
                    y1 = windowHeight - 2;
                }
            } else if (orientation === 'right') {
                angledTopSize = parseFloat(doorData?.frameData?.[angledTrapezoidKey]?.rightSize_d);
                y1 = 0;
                // Check for NaN explicitly because 0 is a valid value
                y2 = (!isNaN(angledTopSize) && angledTopSize !== undefined) ? angledTopSize : (windowHeight - 1) / 2;

                // Validate y2 to ensure valid trapezoid: must be >= 0 and < windowHeight
                if (y2 < 0) {
                    y2 = 0;
                } else if (y2 >= windowHeight - 1) {
                    y2 = windowHeight - 2;
                }
            } else {
                y1 = (windowHeight - 1) / 2;
                y2 = (windowHeight - 1) / 2;
            }

         innerPoints = shapeAngledTopTrapezoid(svg, d, xOffset, initY, [
                {
                    w: windowWidth,
                    h: windowHeight - 1,
                    y1: y1,  // height offset for left side top point (0 = at top)
                    y2: y2,  // height offset for right side top point (middle)
                    color: glassColor,
                    glassType: doorData?.glass?.glassType1.label,
                    glassDesign: glassDesign,
                    frameWidth,
                    frameColor: finishColor,
                    lineFrameColor: 'black',
                    lineFrameThickness: 1,
                    outerStrokeColor: 'black',
                    outerStrokeWidth: 1,
                    middleFrame: true,
                    orientation: orientation  // or 'left'
                }
            ], {
                labelText: `\u00A0${indexWindow} ✔`,
                subFrame: true,
                labelOptions: { fontSize: 24, fontColor: 'green', fontOpacity: 0.2 },
                drawFrame: true
            });
        }  
        if (shape === 'Octagon') {
            innerPoints = shapeOctagon(svg, d, xOffset, initY, [
                {
                    w: windowWidth,
                    h: windowHeight - 1,
                    color: glassColor,
                    glassType: doorData?.glass?.glassType1.label,
                    glassDesign: glassDesign,
                    frameWidth,
                    frameColor: finishColor,
                    lineFrameColor: 'black',
                    lineFrameThickness: 1,
                    outerStrokeColor: 'black',
                    outerStrokeWidth: 1,
                    middleFrame: true
                }
            ], {
                labelText: `\u00A0${indexWindow} ✔`,
                subFrame: true,
                labelOptions: {
                    fontSize: 24,
                    fontColor: 'green',
                    fontOpacity: 0.2
                },
                drawFrame: true
            });
        }
        if (shape === 'Eyebrow') {
            innerPoints = shapeEyebrow(svg, xOffset, initY - 1, {
                w: windowWidth,
                h: 0,
                archHeight: windowHeight,
                frameWidth: frameWidth,
                frameColor: finishColor,
                lineFrameColor: 'black',
                outerStrokeColor: 'black',
                outerStrokeWidth: 1,
                color: doorData?.glass?.glassType1.value || '#EDF9FF',
                includeInnerTopLine: false,
                glassType: doorData?.glass?.glassType1.label,
                glassDesign: glassDesign
            });
        }
        if (shape === 'HalfEyebrow') {
            innerPoints = shapeHalfEyebrow(svg, d, xOffset, initY - 0, [
                {
                    w: windowWidth,
                    h: windowHeight ,
                    color: glassColor,
                    glassType: doorData?.glass?.glassType1.label,
                    glassDesign: glassDesign,
                    frameWidth,
                    frameColor: finishColor,
                    lineFrameColor: 'black',
                    lineFrameThickness: 1,
                    outerStrokeColor: 'black',
                    outerStrokeWidth: 1,
                    middleFrame: true,
                    orientation: orientation
                }
            ], {
                labelText: `\u00A0${indexWindow} ✔`,
                subFrame: true,
                labelOptions: {
                    fontSize: 24,
                    fontColor: 'green',
                    fontOpacity: 0.2
                },
                drawFrame: true
            });
        }
        if (shape === 'Pentagon') {
            shapePentagon(svg, d, xOffset, initY, [
                {
                    w: windowWidth,
                    h: windowHeight - 1,
                    color: glassColor,
                    glassType: doorData?.glass?.glassType1.label,
                    glassDesign: glassDesign,
                    frameWidth,
                    frameColor: finishColor,
                    lineFrameColor: 'black',
                    lineFrameThickness: 1,
                    outerStrokeColor: 'black',
                    outerStrokeWidth: 1,
                    orientation: 'up'
                }
            ], {
                labelText: `\u00A0${indexWindow} ✔`,
                subFrame: true,
                labelOptions: {
                    fontSize: 24,
                    fontColor: 'green',
                    fontOpacity: 0.2
                },
                drawFrame: true
            });
        }  
        if (shape === 'Rectangle') {
            dN_Rect(svg, d, xOffset , initY, [
                {
                    w: windowWidth, //2*adjustedWidth + 2*frameWidth - 1,
                    h: windowHeight - 1,
                    color: glassColor || 'lightblue',
                    glassType: doorData?.glass?.glassType1.label,
                    glassDesign: glassDesign,
                    frameWidth,
                    frameColor: finishColor,
                    lineFrameColor: 'black',
                    lineFrameThickness: 1,
                    outerStrokeColor: 'black',
                    outerStrokeWidth: 1,
                    halfCorners: true
                }
            ], {
                labelText: `\u00A0${indexWindow} ✔`,
                subFrame: true,
                labelOptions: {
                    fontSize: 24,
                    fontColor: 'green',
                    fontOpacity: 0.2
                }
            });
        }
        

        // Calculate y1, y2 for AngledTopTrapezoid grills
        let grillY1 = 0;
        let grillY2 = 0;
        if (shape === 'AngledTopTrapezoid') {
            if (orientation === 'left'){
                grillY1 = (windowHeight - 1) / 2;
                grillY2 = 0;
            } else {
                grillY1 = 0;
                grillY2 = (windowHeight - 1) / 2;
            }
        }

        drawGrills(svg, indexWindow,
                   xOffset , initY,
                   windowWidth, /*2*adjustedWidth + 2*frameWidth,*/
                   windowHeight,
                   frameWidth,
                   doorData, top, shape, orientation, grillY1, grillY2, innerPoints );
    }
}//drawPicture
